import type * as LabelPrimitive from '@radix-ui/react-label';
import type { ComponentProps } from 'react';
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { createContext, useId, use } from 'react';
import { Controller, FormProvider, useFormContext, useFormState } from 'react-hook-form';

import { Label } from '@/components/ui/label/Label';

export const Form = FormProvider;

type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};

interface FormErrorMessageProps extends ComponentProps<'p'> {
  name: string;
}

export const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

export function FormField<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({ ...props }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext>
  );
}

export function useErrorMessage(name: string) {
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name });
  return getFieldState(name, formState);
}

const useFormField = () => {
  const fieldContext = use(FormFieldContext);
  const itemContext = use(FormItemContext);
  const fieldState = useErrorMessage(fieldContext.name);

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

export function FormItem(formItemProps: ComponentProps<'div'>) {
  const { className, ...props } = formItemProps;
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <FormItemContent className={className} {...props} />
    </FormItemContext.Provider>
  );
}

export function FormItemContent(formItemContentProps: ComponentProps<'div'>) {
  const { className, ...props } = formItemContentProps;
  const { error } = useFormField();

  return <div data-slot="form-item" aria-invalid={!!error} className={cn('group grid w-full gap-2', className)} {...props} />;
}

export function FormLabel(formLabelProps: ComponentProps<typeof LabelPrimitive.Root>) {
  const { className, ...props } = formLabelProps;
  const { formItemId } = useFormField();

  return <Label data-slot="form-label" className={cn('group-aria-invalid:text-destructve font-normal', className)} htmlFor={formItemId} {...props} />;
}

export function FormControl(formControlProps: ComponentProps<typeof Slot>) {
  const { ...props } = formControlProps;
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return <Slot data-slot="form-control" id={formItemId} aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`} aria-invalid={!!error} {...props} />;
}

export function FormDescription(formDescriptionProps: ComponentProps<'p'>) {
  const { className, ...props } = formDescriptionProps;
  const { formDescriptionId } = useFormField();

  return <p data-slot="form-description" id={formDescriptionId} className={cn('text-muted-foreground text-sm', className)} {...props} />;
}

export function FormMessage(formMessageprops: ComponentProps<'p'>) {
  const { className, children, ...props } = formMessageprops;

  const { error, formMessageId } = useFormField();
  const body = error ? String(error.message ?? '') : null;

  return (
    <p data-slot="form-message" id={formMessageId} className={cn('text-destructive relative min-h-5 text-xs', className)} {...props}>
      {children}
      {body}
    </p>
  );
}

export function ErrorMessage(formErrorMessageProps: FormErrorMessageProps) {
  const { className, name, ...props } = formErrorMessageProps;
  const { error } = useErrorMessage(name);

  return (
    <p data-slot="form-error-message" data-name={name} className={cn('text-destructive relative min-h-5 text-xs', className)} {...props}>
      {error?.message}
    </p>
  );
}
